import { Form, Head, usePage } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";

import GoogleSignInButton from "@/components/google-sign-in-button";
import InputError from "@/components/input-error";
import TextLink from "@/components/text-link";
import TurnstileWidget from "@/components/turnstile-widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/layouts/auth-layout";
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes";

interface LoginProps {
  turnstile_site_key?: string | null;
}

export default function Login({ turnstile_site_key }: LoginProps) {
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRequired = Boolean(turnstile_site_key);
  const { errors } = usePage<{ errors?: Record<string, string> }>().props;

  return (
    <AuthLayout title="Log in to your account" description="Enter your email and password below to log in">
      <Head title="Log in" />
      <div className="flex flex-col gap-6">
        <TurnstileWidget
          siteKey={turnstile_site_key ?? ""}
          action="sign_in"
          onTokenChange={setTurnstileToken}
          error={errors?.turnstile}
        />
        <GoogleSignInButton
          actionPath="/sign_in/google"
          turnstileToken={turnstileToken}
          turnstileRequired={turnstileRequired}
        />

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="text-muted-foreground bg-background px-2">Or continue with</span>
          </div>
        </div>

        <Form method="post" action={signInPath()} resetOnSuccess={["password"]} className="flex flex-col gap-6">
          {({ processing, errors }) => (
            <>
              <div className="grid gap-6">
                <input type="hidden" name="cf-turnstile-response" value={turnstileToken} />

                <div className="grid gap-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoFocus
                    tabIndex={1}
                    autoComplete="email"
                    placeholder="email@example.com"
                  />
                  <InputError message={errors.email} />
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password</Label>
                    <TextLink href={newIdentityPasswordResetPath()} className="ml-auto text-sm" tabIndex={5}>
                      Forgot password?
                    </TextLink>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    required
                    tabIndex={2}
                    autoComplete="current-password"
                    placeholder="Password"
                  />
                  <InputError message={errors.password} />
                </div>

                <Button
                  type="submit"
                  className="mt-4 w-full"
                  tabIndex={4}
                  disabled={processing || (turnstileRequired && !turnstileToken)}
                >
                  {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  Log in
                </Button>
              </div>

              <div className="text-muted-foreground text-center text-sm">
                Don&apos;t have an account?{" "}
                <TextLink href={signUpPath()} tabIndex={5}>
                  Sign up
                </TextLink>
              </div>
            </>
          )}
        </Form>
      </div>
    </AuthLayout>
  );
}
